import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';

import { WorkerStatusEnum } from '../enums/worker-status.enum';
import { TelegramMessageEntry } from '../models/telegram-message.model';
import { HistoryLoadStateDto } from '../pages/account-info/models/history-load-state.interface';
import { StatusHistory } from '../pages/account-info/models/status-history.interface';
import { AccountManagemntTableItem } from '../pages/account-management/models/account-management-table-item.interface';
import { TelegramAccount } from '../pages/telegram-account/models/telegram-account.interface';

interface PhonePayload {
  phone: string;
}

interface CodePayload extends PhonePayload {
  code: string;
}

interface SessionPayload extends PhonePayload {
  sessionId: string;
}

@Injectable({ providedIn: 'root' })
export class TelegramAccountService {
  private http = inject(HttpClient);

  getAll(): Observable<AccountManagemntTableItem[]> {
    return this.http.get<AccountManagemntTableItem[]>(`${environment.apiUrl}/tg/accounts`);
  }

  getAccount(id: string): Observable<TelegramAccount> {
    return this.http.get<TelegramAccount>(`${environment.apiUrl}/vault/accounts/${id}`);
  }

  getAccountByPhone(phone: string): Observable<AccountManagemntTableItem | undefined> {
    return this.http
      .get<AccountManagemntTableItem[]>(`${environment.apiUrl}/tg/accounts?search=${encodeURIComponent(phone)}`)
      .pipe(map((accounts) => accounts[0]));
  }

  getAllAvailableToConnect(): Observable<TelegramAccount[]> {
    return this.http.get<TelegramAccount[]>(`${environment.apiUrl}/TelegramAccount/GetAllAvailableAccounts`);
  }

  connect(phone: string): Observable<void> {
    const payload: PhonePayload = { phone };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/start`, payload);
  }

  sendCode(phone: string, code: string): Observable<void> {
    const payload: CodePayload = { phone, code };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/send-code`, payload);
  }

  disconnect(phone: string): Observable<void> {
    const payload: PhonePayload = { phone };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/stop`, payload);
  }

  getAllSessions(phone: string): Observable<{ status: WorkerStatusEnum }> {
    const payload: PhonePayload = { phone };
    return this.http.post<{ status: WorkerStatusEnum }>(`${environment.apiUrl}/tg/bots/request-sessions`, payload);
  }

  getStatusHistories(phone: string): Observable<StatusHistory[]> {
    return this.http.get<StatusHistory[]>(`${environment.apiUrl}/tg/accounts/${phone}/history`);
  }

  getHistoryLoadState(phone: string): Observable<HistoryLoadStateDto | null> {
    return this.http.get<HistoryLoadStateDto | null>(`${environment.apiUrl}/tg/accounts/${phone}/history-load`);
  }

  downloadHistory(phone: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/download-history`, { phone });
  }

  reloadHistory(phone: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/reload-history`, { phone });
  }

  resumeHistory(phone: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/resume-history`, { phone });
  }

  stopHistory(phone: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/stop-history`, { phone });
  }

  terminateSession(phone: string, session_id: string): Observable<void> {
    const payload: SessionPayload = { phone, sessionId: session_id };
    return this.http.post<void>(`${environment.apiUrl}/tg/bots/terminate-session`, payload);
  }

  sendMessage(payload: {
    phone: string;
    phoneDestinations: string[];
    messages: TelegramMessageEntry[];
  }): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/tg/message/send`, payload);
  }
}
